import React from 'react';
import { Phone, Mail, UserCheck, Building2, MapPin, Award } from 'lucide-react';
import { SheetAdvisor } from './types';

interface PropertyAgentContactCardProps {
  advisor: SheetAdvisor;
  showContactInfo?: boolean;
}

export const PropertyAgentContactCard: React.FC<PropertyAgentContactCardProps> = ({
  advisor,
  showContactInfo = true,
}) => {
  const canShowContact = showContactInfo && advisor.showContactInfo !== false;

  return (
    <div className="bg-[#ffffff] rounded-2xl border border-[#e2e8f0] p-3 shadow-xs">
      {/* Section Header */}
      <div className="flex items-center justify-between gap-2 mb-2 pb-1.5 border-b border-[#f1f5f9]">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-3.5 rounded-full bg-[#f59e0b]" />
          <h3 className="text-xs font-black tracking-wider text-[#0a2540] uppercase">
            Datos del Asesor Inmobiliario
          </h3>
        </div>
        <span className="inline-flex items-center gap-1 text-[9px] font-bold text-[#059669] bg-[#ecfdf5] px-1.5 py-0.5 rounded border border-[#a7f3d0]">
          <UserCheck className="w-2.5 h-2.5" />
          <span>Verificado</span>
        </span>
      </div>

      {/* Advisor Profile Header */}
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-full bg-[#0a2540] text-white flex items-center justify-center font-extrabold text-[11px] flex-shrink-0 ring-1 ring-[#e2e8f0]">
          {advisor.initials}
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-xs font-bold text-[#0f172a] leading-normal">{advisor.name}</h4>
          <p className="text-[10px] font-semibold text-[#0a2540] flex items-center gap-1 mt-0.5 leading-normal">
            <Award className="w-2.5 h-2.5 text-[#d97706] flex-shrink-0" />
            <span className="leading-normal">{advisor.title}</span>
          </p>
        </div>
      </div>

      {/* Details: listed one by one in each row */}
      <div className="space-y-1.5 pt-1 border-t border-[#f1f5f9]">
        {/* Teléfono / WhatsApp: solo broker o broker premium */}
        {canShowContact && advisor.phone && (
          <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-[#f8fafc] border border-[#f1f5f9]">
            <div className="flex items-center gap-1.5 min-w-0">
              <Phone className="w-3 h-3 text-[#d97706] flex-shrink-0" />
              <span className="text-[9px] font-bold text-[#64748b] uppercase leading-normal">Teléfono / WhatsApp</span>
            </div>
            <span className="text-[11px] font-bold text-[#0f172a] font-mono leading-normal">{advisor.phone}</span>
          </div>
        )}

        {/* Correo Electrónico: solo broker o broker premium */}
        {canShowContact && advisor.email && (
          <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-[#f8fafc] border border-[#f1f5f9]">
            <div className="flex items-center gap-1.5 min-w-0">
              <Mail className="w-3 h-3 text-[#4338ca] flex-shrink-0" />
              <span className="text-[9px] font-bold text-[#64748b] uppercase leading-normal">Correo Electrónico</span>
            </div>
            <span className="text-[11px] font-bold text-[#0f172a] max-w-[60%] leading-normal">{advisor.email}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-[#f8fafc] border border-[#f1f5f9]">
          <div className="flex items-center gap-1.5 min-w-0">
            <Building2 className="w-3 h-3 text-[#0284c7] flex-shrink-0" />
            <span className="text-[9px] font-bold text-[#64748b] uppercase leading-normal">Inmobiliaria / Broker</span>
          </div>
          <span className="text-[11px] font-bold text-[#0f172a] max-w-[60%] leading-normal">
            {advisor.agencyName || 'Red Broker Ciencuadras'}
          </span>
        </div>

        <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-[#f8fafc] border border-[#f1f5f9]">
          <div className="flex items-center gap-1.5 min-w-0">
            <MapPin className="w-3 h-3 text-[#16a34a] flex-shrink-0" />
            <span className="text-[9px] font-bold text-[#64748b] uppercase leading-normal">Zona de Cobertura</span>
          </div>
          <span className="text-[11px] font-bold text-[#0f172a] max-w-[60%] leading-normal">
            {advisor.location || 'Bogotá D.C. y Alrededores'}
          </span>
        </div>
      </div>
    </div>
  );
};
